import type { WorkflowState } from '@compass/client/types/case_record';

import type { FieldGroup } from '@/types/formSchema';

import { formatDate as formatDateUtil } from './date';
import { getDefaultValueForField, validateField } from './formSchema';
import { calculateSLA } from './sla';
import { getStatusColor, getStatusLabel } from './status';

/**
 * Calculate SLA elapsed time
 * @param workflowLastStartedAt - Workflow last started timestamp
 * @param workflowDurationSeconds - Accumulated workflow running time in seconds (excludes paused time)
 * @param slaMinutes - SLA duration in minutes from workflow configuration
 * @param isBreached - SLA breach flag from backend
 * @param workflowState - Current workflow state (if WAITING/paused, don't accumulate time)
 * @returns Object with formatted time string and breach status (wall-clock time minus paused time)
 */
export const calculateSLATime = (
  workflowLastStartedAt: string | undefined,
  workflowDurationSeconds: number | undefined,
  slaMinutes: number | undefined,
  isBreached?: boolean,
  workflowState?: WorkflowState,
): {
  timeString: string;
  percentageElapsed: number;
  isBreached: boolean;
  remainingMinutes?: number;
} => {
  const slaResult = calculateSLA(
    workflowLastStartedAt,
    workflowDurationSeconds,
    slaMinutes,
    isBreached,
    workflowState,
  );
  return {
    timeString: slaResult.timeString,
    percentageElapsed: slaResult.percentageElapsed,
    isBreached: slaResult.isBreached,
    remainingMinutes: slaResult.remainingMinutes,
  };
};

// Format status for display - delegates to status utils
export const formatStatus = (state: string | undefined, workflow?: any): string => {
  return getStatusLabel(state, workflow);
};

// Get status colors based on state - delegates to status utils
export const getStatusColors = (
  state: string | undefined,
  workflow?: any,
  activeCallId?: string,
) => {
  return getStatusColor(state, workflow, activeCallId);
};

// Format case ID for display
// If code is provided, display it; otherwise fall back to the MongoDB ObjectID
export const formatCaseId = (caseId: string | undefined, code?: string): string => {
  if (code) {
    return code;
  }

  if (!caseId) {
    return 'N/A';
  }

  return caseId;
};

// Format date for display - delegates to date utils
export const formatDate = (date: string | undefined): string => {
  return formatDateUtil(date);
};

/**
 * Separate metadata group from other field groups.
 * Metadata group contains fields like case_type that should be displayed differently.
 */
export function separateMetadataGroup<T extends { label: string }>(
  inputGroups: T[],
): {
  metadataGroup: T | undefined;
  otherGroups: T[];
} {
  const metadataGroup = inputGroups.find(group => group.label.toLowerCase() === 'metadata');
  const otherGroups = inputGroups.filter(group => group.label.toLowerCase() !== 'metadata');
  return { metadataGroup, otherGroups };
}

/**
 * Initialize form data from field groups with default values
 * Preserves any fields from existing data that are not in the UI schema
 */
export function initializeFormData(groups: FieldGroup[], existingData?: any): Record<string, any> {
  const data: Record<string, any> = {};

  // First, preserve all fields from existingData
  if (existingData) {
    Object.assign(data, existingData);
  }

  // Then, override with values from UI schema fields (ensuring proper type-aware defaults)
  groups.forEach(group => {
    group.fields.forEach(field => {
      // Use existing data if available, otherwise use type-aware default
      data[field.id] = existingData?.[field.id] ?? getDefaultValueForField(field);
    });
  });

  return data;
}

/**
 * Validate all fields in a specific group
 */
export function validateGroup(
  group: FieldGroup,
  formData: Record<string, any>,
): Record<string, string> {
  const errors: Record<string, string> = {};
  group.fields.forEach(field => {
    const value = formData[field.id];
    const error = validateField(field, value);
    if (error) {
      errors[field.id] = error;
    }
  });
  return errors;
}
